import { useState } from "react";
import { useTranslation } from "react-i18next";

interface InstallStep {
  label: string;
  content: string;
  code?: string;
  note?: string;
}

interface PlatformNote {
  title: string;
  description: string;
  steps: InstallStep[];
}

type PlatformTab = "macos" | "linux" | "windows";

function usePlatformNotes(): Record<PlatformTab, PlatformNote> {
  const { t } = useTranslation();

  return {
    macos: {
      title: t('downloads.installationNotes.macos.title'),
      description: t('downloads.installationNotes.macos.description'),
      steps: [
        {
          label: t('downloads.installationNotes.macos.step1Label'),
          content: t('downloads.installationNotes.macos.step1Content'),
        },
        {
          label: t('downloads.installationNotes.macos.step2Label'),
          content: t('downloads.installationNotes.macos.step2Content'),
          note: t('downloads.installationNotes.macos.step2Note'),
        },
        {
          label: t('downloads.installationNotes.macos.doneLabel'),
          content: t('downloads.installationNotes.macos.doneContent'),
        },
      ],
    },
    linux: {
      title: t('downloads.installationNotes.linux.title'),
      description: t('downloads.installationNotes.linux.description'),
      steps: [
        {
          label: t('downloads.installationNotes.linux.appImageLabel'),
          content: t('downloads.installationNotes.linux.appImageContent'),
          code: "chmod +x CloseCode ADE-*.AppImage\n./CloseCode ADE-*.AppImage",
        },
        {
          label: t('downloads.installationNotes.linux.debLabel'),
          content: t('downloads.installationNotes.linux.debContent'),
          code: "sudo dpkg -i closecode-electron_*_amd64.deb",
        },
      ],
    },
    windows: {
      title: t('downloads.installationNotes.windows.title'),
      description: t('downloads.installationNotes.windows.description'),
      steps: [
        {
          label: t('downloads.installationNotes.windows.installerLabel'),
          content: t('downloads.installationNotes.windows.installerContent'),
        },
        {
          label: t('downloads.installationNotes.windows.portableLabel'),
          content: t('downloads.installationNotes.windows.portableContent'),
        },
      ],
    },
  };
}

export function InstallationNotes() {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState<PlatformTab>("macos");
  const platformNotes = usePlatformNotes();

  const tabs: { id: PlatformTab; label: string }[] = [
    { id: "macos", label: t("downloads.platforms.macos.name") },
    { id: "linux", label: t("downloads.platforms.linux.name") },
    { id: "windows", label: t("downloads.platforms.windows.name") },
  ];

  const notes = platformNotes[activeTab];

  return (
    <div className="bg-dark-card rounded-xl border border-dark-border overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-dark-hover transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-dark-bg">
            <svg
              className="w-5 h-5 text-warm-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-warm-50">{t('downloads.installationNotes.title')}</h3>
            <p className="text-sm text-warm-400">
              {t('downloads.installationNotes.subtitle')}
            </p>
          </div>
        </div>
        <svg
          className={`w-5 h-5 text-warm-500 transition-transform ${isExpanded ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isExpanded && (
        <div className="border-t border-dark-border">
          <div className="flex border-b border-dark-border">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? "text-warm-50 border-b-2 border-blue-500"
                    : "text-warm-500 hover:text-warm-100"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-6">
            {activeTab === "macos" && (
              <div className="mb-4 px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <div className="flex gap-2">
                  <svg
                    className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                    />
                  </svg>
                  <p className="text-sm text-amber-200">
                    {t('downloads.installationNotes.macosWarning')}
                  </p>
                </div>
              </div>
            )}

            <h4 className="text-base font-semibold text-warm-100 mb-1">{notes.title}</h4>
            <p className="text-sm text-warm-400 mb-4">{notes.description}</p>

            <div className="space-y-4">
              {notes.steps.map((step, index) => (
                <div key={index} className="space-y-2">
                  <h5 className="text-sm font-medium text-warm-200">{step.label}</h5>
                  <p className="text-sm text-warm-400">{step.content}</p>
                  {step.code && (
                    <code className="block text-sm text-warm-300 bg-dark-bg rounded px-4 py-3 font-mono whitespace-pre select-all">
                      {step.code}
                    </code>
                  )}
                  {"note" in step && step.note && (
                    <p className="text-xs text-warm-500 italic">{step.note}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
